import { CalendarIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";

import { interviewTypeKindClasses } from "@/lib/constants";
import { dashboardScheduleWeek } from "@/lib/dashboardScheduleWeek";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { DashboardInterview, DashboardScheduleDaySection } from "@/types";

const MAX_RAIL_DAYS = 6;

// Day-section keys are "YYYY-MM-DD" (local); parse back into a Date for the
// rail's compact weekday/day-number header.
const parseDayKey = (key: string): Date => {
  const [year, month, day] = key.split("-").map(Number);
  return new Date(year, month - 1, day);
};

export const ScheduleCalendarRail = ({
  interviews,
  onExpand,
}: {
  interviews: DashboardInterview[];
  onExpand: () => void;
}) => {
  const sections = dashboardScheduleWeek.buildDaySections(interviews);
  const totalCount = sections.reduce((sum, s) => sum + s.interviews.length, 0);
  const populated = sections
    .filter((s) => s.interviews.length > 0)
    .slice(0, MAX_RAIL_DAYS);

  const renderEmpty = () => (
    <Column className="items-center gap-1.5 pt-2 text-slate-400">
      <CalendarIcon className="h-4 w-4" />
      <Text className="text-[10px] font-medium leading-tight">None</Text>
    </Column>
  );

  const render = () => (
    <Button
      variant="ghost"
      onClick={onExpand}
      aria-label="Expand schedule"
      className="group h-full w-[44px] shrink-0 flex-col items-stretch justify-start gap-0 overflow-hidden rounded-xl bg-slate-100 p-0 hover:bg-slate-200"
    >
      <Row className="mt-2 h-7 shrink-0 items-center justify-center text-slate-500 group-hover:text-slate-700">
        <CalendarIcon className="h-4 w-4" />
      </Row>
      <Column className="gap-3 px-1 pb-2 pt-1">
        {totalCount === 0
          ? renderEmpty()
          : populated.map((section) => (
              <ScheduleCalendarRailDay key={section.key} section={section} />
            ))}
      </Column>
    </Button>
  );

  return render();
};

const ScheduleCalendarRailDay = ({
  section,
}: {
  section: DashboardScheduleDaySection;
}) => {
  const date = parseDayKey(section.key);
  const isToday = section.listLabel === "Today";
  const dayHeaderLabel = isToday
    ? "Today"
    : date.toLocaleDateString("en-US", { weekday: "short" });

  const render = () => (
    <Column className="items-center gap-1">
      <Column className="items-center">
        <Text
          className={cn(
            "text-[9px] font-semibold uppercase leading-tight tracking-wider",
            isToday ? "text-slate-800" : "text-slate-400",
          )}
        >
          {dayHeaderLabel}
        </Text>
        <Text className="text-[10px] font-semibold tabular-nums leading-tight text-slate-500">
          {date.getDate().toString()}
        </Text>
      </Column>
      {section.interviews.map((interview) => (
        <ScheduleCalendarRailBadge key={interview.id} interview={interview} />
      ))}
    </Column>
  );

  return render();
};

const ScheduleCalendarRailBadge = ({
  interview,
}: {
  interview: DashboardInterview;
}) => {
  const initials = formatting.initials(
    interview.candidacy?.firstName,
    interview.candidacy?.lastName,
  );
  const kind = interviewTypeKindClasses[interview.type];

  const render = () => (
    <span
      className={cn(
        "inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-[10px] font-bold",
        kind.avatar,
      )}
    >
      {initials}
    </span>
  );

  return render();
};
